import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';
import {Column, useFlexLayout, useTable} from 'react-table';

import CenteredSpinner from 'app/components/CenteredSpinner';
import TeamMemberModal from 'app/components/GlobalSettings/TeamMemberModal';
import Gravatar from 'app/components/Gravatar';
import InviteUsersModal from 'app/components/InviteUsersModal/InviteUsersModal';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {OrgUsersActions, WithOrgUsers} from 'app/providers/OrgUsersProvider';
import {isLensPlaygroundUser} from 'app/utils/featureFlags';
import {alphanumericSort} from 'app/utils/featureUtils';
import * as inviteUtils from 'app/utils/inviteUtils';
import {getIsOrgOverSeatLimit} from 'app/utils/organizationUtils';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

import * as GlobalSettings from './GlobalSettings';
import cs from './TeamView.module.scss';

const TeamViewTable: React.FunctionComponent<
  React.PropsWithChildren<{
    organizationUsers: ApiOrganizationUser[];
    getCanEditUser: (userId: string) => boolean;
    onEditUser: (userId: string) => void;
  }>
> = ({organizationUsers, getCanEditUser, onEditUser}) => {
  const columns = React.useMemo<Column<ApiOrganizationUser>[]>(
    () => [
      {
        Header: 'Name',
        // Accessors are only used for sorting and filtering. While we show both
        // the name and email address in this column, we would likely only sort
        // and filter by the user’s name.
        accessor: (user) => user.name,
        Cell: ({cell}) => {
          const user = cell.row.original;
          const name = user.name;
          const email = user.email;

          return (
            <div className={cs.tableNameCell}>
              <Gravatar email={email} />
              <div className={cs.tableNameCellText}>
                <div>
                  {name}
                  {user.mfaEnabled && (
                    <B.Tooltip content="Multi-factor authentication is enabled for this user">
                      <B.Icon className={cs.tableNameCellMfaEnabledIcon} icon="key" color="gold" />
                    </B.Tooltip>
                  )}
                </div>
                <div className={cs.tableNameCellTextEmail}>{email}</div>
              </div>
            </div>
          );
        },
        width: 3,
      },
      {
        Header: 'Role',
        accessor: (user) => inviteUtils.ROLE_NAMES[user.role],
        width: 1,
      },
      {
        id: 'Edit',
        accessor: (user) => user.id,
        Cell: ({cell}) => {
          const userId = cell.value;

          return (
            getCanEditUser(userId) && (
              <div className={cs.tableEditCell}>
                <a onClick={() => onEditUser(userId)}>Edit</a>
              </div>
            )
          );
        },
        width: 1,
      },
    ],
    [getCanEditUser, onEditUser]
  );

  const data = React.useMemo<ApiOrganizationUser[]>(
    () =>
      organizationUsers
        .filter((u) => !u!.deletedLocally)
        .sort((a, b) => alphanumericSort(a.name, b.name)),

    [organizationUsers]
  );

  const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow} = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [{id: 'Name'}],
      },
    },
    useFlexLayout
  );

  return (
    <div className={cs.scrollRegion}>
      <table className={cs.table} {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={i}>
              {headerGroup.headers.map((column, i) => (
                <th {...column.getHeaderProps()} key={i}>
                  <div>{column.render('Header')}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);

            return (
              <tr {...row.getRowProps()} key={i}>
                {row.cells.map((cell, i) => (
                  <td {...cell.getCellProps()} key={i}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TeamView: React.FunctionComponent<
  React.PropsWithChildren<{
    actions: OrgUsersActions;
    organizationUsers: ApiOrganizationUser[] | null;
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
  }>
> = ({organizationUsers, organization, profile, actions}) => {
  const [isInviteUsersModalOpen, setIsInviteUsersModalOpen] = React.useState<boolean>(false);
  const [editingUserId, setEditingUserId] = React.useState<string | null>(null);

  if (!organizationUsers) {
    return <CenteredSpinner />;
  }

  const isOrgOverSeatLimit = getIsOrgOverSeatLimit(organization, organizationUsers.length);

  const userCanInviteOthers = inviteUtils.canInviteOthers(profile.get('role'));

  return (
    <GlobalSettings.Body
      title="Team"
      titleActions={
        userCanInviteOthers && (
          <>
            <B.Tooltip
              disabled={!isOrgOverSeatLimit}
              content={
                <>
                  It looks like you've filled all the user seats included in your plan. Reach out to{' '}
                  <a href="mailto:lens@upstream.tech">lens@upstream.tech</a> to upgrade your
                  subscription.
                </>
              }
            >
              <B.Button
                intent={B.Intent.PRIMARY}
                text="Invite"
                onClick={() => setIsInviteUsersModalOpen(true)}
                disabled={isOrgOverSeatLimit}
              />
            </B.Tooltip>

            <InviteUsersModal
              isOpen={isInviteUsersModalOpen}
              onClose={() => setIsInviteUsersModalOpen(false)}
              permissions={['orders', 'teamManagement', 'dashboard', 'properties']}
              organizationId={organization.get('id')}
            />
          </>
        )
      }
    >
      <TeamViewTable
        organizationUsers={organizationUsers}
        getCanEditUser={(userId) => {
          const id = profile.get('id');
          const role = profile.get('role');
          // Owners can edit anyone. Anyone can edit themselves.
          return (isOwnerOrAdmin(role) || id === userId) && !isLensPlaygroundUser(profile);
        }}
        onEditUser={(userId) => setEditingUserId(userId)}
      />

      <TeamMemberModal
        editingUserId={editingUserId}
        organizationId={organization.get('id')}
        profile={profile}
        organizationUsers={organizationUsers}
        actions={actions}
        onClose={() => setEditingUserId(null)}
      />
    </GlobalSettings.Body>
  );
};

const TeamViewDataProvider: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
  }>
> = ({profile, organization}) => (
  // Fetches the latest users and prevents stale ones from being shown.
  <WithOrgUsers refreshOnMount="clear">
    {(organizationUsers, _, actions) => (
      <TeamView
        actions={actions}
        organizationUsers={organizationUsers}
        organization={organization}
        profile={profile}
      />
    )}
  </WithOrgUsers>
);

export default TeamViewDataProvider;
